import { useEffect, useRef, useState } from "react";
import { Link, Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Zap, Users, Wrench, CalendarClock, MessageSquareText, LogOut, LayoutDashboard, BarChart3, Calendar, Bell, X, Package, UserCog, Activity, ScanLine, ShoppingCart, ShieldCheck, Bike, ShoppingBag, FileText } from "lucide-react";

const menu = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/clienti", label: "Clienti", icon: Users },
  { to: "/admin/reparatii", label: "Reparatii", icon: Wrench },
  { to: "/admin/vanzari", label: "Vanzari", icon: ShoppingCart },
  { to: "/admin/rezervari", label: "Rezervari", icon: CalendarClock },
  { to: "/admin/garantii", label: "Garantii", icon: ShieldCheck },
  { to: "/admin/produse", label: "Produse", icon: Bike },
  { to: "/admin/comenzi", label: "Comenzi", icon: ShoppingBag },
  { to: "/admin/facturi", label: "Facturi", icon: FileText },
  { to: "/admin/calendar", label: "Calendar", icon: Calendar },
  { to: "/admin/piese", label: "Piese", icon: Package },
  { to: "/admin/scan", label: "Scan", icon: ScanLine },
  { to: "/admin/rapoarte", label: "Rapoarte", icon: BarChart3 },
  { to: "/admin/sms", label: "SMS Log", icon: MessageSquareText },
  { to: "/admin/activity", label: "Activity", icon: Activity, ownerOnly: true },
  { to: "/admin/utilizatori", label: "Utilizatori", icon: UserCog, ownerOnly: true },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [authed, setAuthed] = useState(null);
  const [role, setRole] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [showNotifs, setShowNotifs] = useState(false);
  const [newOrders, setNewOrders] = useState(0);
  const prevOrders = useRef(null);

  const playOrderSound = () => {
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      [880, 1320].forEach((freq, i) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain); gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = "sine";
        gain.gain.setValueAtTime(0.15, ctx.currentTime + i * 0.18);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.18 + 0.35);
        osc.start(ctx.currentTime + i * 0.18);
        osc.stop(ctx.currentTime + i * 0.18 + 0.4);
      });
    } catch { /* audio blocked */ }
  };

  useEffect(() => {
    if (!localStorage.getItem("kk_token")) { setAuthed(false); return; }
    api.get("/auth/me").then((r) => { setAuthed(true); setRole(r.data.role); }).catch(() => setAuthed(false));
  }, [location.pathname]);

  useEffect(() => {
    if (!authed) return;
    const fetch = () => api.get("/admin/notifications").then((r) => {
      setNotifications(r.data.items || []);
      const n = r.data.new_orders || 0;
      setNewOrders(n);
      if (prevOrders.current !== null && n > prevOrders.current) {
        playOrderSound();
        toast.success("🛒 Comanda noua in magazin!", { duration: 8000 });
      }
      prevOrders.current = n;
    }).catch(() => {});
    fetch();
    const id = setInterval(fetch, 20000);
    return () => clearInterval(id);
  }, [authed, location.pathname]);

  if (authed === false) return <Navigate to="/admin/login" replace />;
  if (authed === null) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const logout = () => { localStorage.removeItem("kk_token"); navigate("/admin/login"); };

  return (
    <div className="min-h-screen flex bg-[#0A0A0A] text-white">
      <aside data-testid="admin-sidebar" className="hidden lg:flex flex-col w-64 border-r border-neutral-900 bg-black sticky top-0 h-screen">
        <div className="p-6 border-b border-neutral-900">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#00E5FF] flex items-center justify-center">
              <Zap className="w-5 h-5 text-black" strokeWidth={3} />
            </div>
            <div className="leading-none">
              <div className="font-display text-base">TROTI-X</div>
              <div className="text-[10px] tracking-[0.25em] text-[#00E5FF] uppercase mt-1">Admin Panel</div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {menu.filter((m) => !m.ownerOnly || role === "admin").map((m) => {
            const active = m.end ? location.pathname === m.to : location.pathname.startsWith(m.to);
            return (
              <Link key={m.to} to={m.to} data-testid={`admin-nav-${m.label.toLowerCase()}`}
                className={`flex items-center gap-3 px-3 py-2.5 text-sm uppercase tracking-wider transition-colors ${active ? "bg-[#00E5FF] text-black" : "text-neutral-300 hover:bg-neutral-900 hover:text-white"}`}>
                <m.icon className="w-4 h-4" /> {m.label}
                {m.to === "/admin/comenzi" && newOrders > 0 && (
                  <span data-testid="nav-orders-badge" className={`ml-auto text-[10px] font-bold min-w-[18px] h-[18px] flex items-center justify-center rounded-full ${active ? "bg-black text-[#00E5FF]" : "bg-[#00E5FF] text-black"}`}>
                    {newOrders}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="p-4 border-t border-neutral-900 space-y-2">
          <Link to="/" className="block text-xs text-neutral-500 hover:text-[#00E5FF] uppercase tracking-widest">← Site public</Link>
          <button onClick={logout} data-testid="admin-logout-btn" className="w-full flex items-center gap-2 text-sm text-neutral-300 hover:text-[#00E5FF] uppercase tracking-wider">
            <LogOut className="w-4 h-4" /> Iesire
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="flex items-center justify-between p-4 border-b border-neutral-900 bg-black">
          <div className="flex items-center gap-2 lg:hidden">
            <Zap className="w-5 h-5 text-[#00E5FF]" />
            <span className="font-display">TROTI-X ADMIN</span>
          </div>
          <div className="hidden lg:block text-xs uppercase tracking-widest text-neutral-500">
            {location.pathname.replace("/admin", "Admin") || "Admin"}
          </div>
          <div className="relative ml-auto flex items-center gap-3">
            <button
              data-testid="notif-bell"
              onClick={() => setShowNotifs((v) => !v)}
              className="relative p-2 hover:text-[#00E5FF] transition-colors"
            >
              <Bell className="w-5 h-5" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 min-w-[16px] h-4 px-1 bg-[#00E5FF] text-black text-[10px] font-bold flex items-center justify-center rounded-full">
                  {notifications.length}
                </span>
              )}
            </button>
            <button onClick={logout} className="text-xs uppercase tracking-wider text-neutral-400 hover:text-[#00E5FF] lg:hidden">Iesire</button>
          </div>

          {showNotifs && (
            <div className="absolute top-14 right-4 w-80 bg-neutral-950 border border-neutral-800 z-50 shadow-2xl">
              <div className="flex items-center justify-between p-3 border-b border-neutral-800">
                <span className="font-display text-sm uppercase">Notificari</span>
                <button onClick={() => setShowNotifs(false)} className="text-neutral-400 hover:text-white"><X className="w-4 h-4" /></button>
              </div>
              <div className="max-h-96 overflow-y-auto">
                {notifications.length === 0 && (
                  <div className="p-6 text-center text-sm text-neutral-500">Nicio notificare. Totul e in regula. ✓</div>
                )}
                {notifications.map((n, i) => (
                  <Link
                    key={i}
                    to={n.link}
                    onClick={() => setShowNotifs(false)}
                    className={`block p-3 border-b border-neutral-900 hover:bg-neutral-900 ${n.level === "danger" ? "border-l-2 border-l-red-500" : "border-l-2 border-l-amber-500"}`}
                  >
                    <div className="text-sm">{n.title}</div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </header>
        <div className="lg:hidden flex overflow-x-auto border-b border-neutral-900 bg-black">
          {menu.filter((m) => !m.ownerOnly || role === "admin").map((m) => (
            <Link key={m.to} to={m.to} className="px-4 py-3 text-xs uppercase whitespace-nowrap text-neutral-300 hover:text-[#00E5FF]">{m.label}</Link>
          ))}
        </div>
        <main className="p-6 lg:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
